import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Button from "../UI/Button";
import GatewayToggleCard from "./GatewayToggleCard";
import CountryPaymentGatewayModal from "./CountryPaymentGatewayModal";
import CountrySelector from "./CountrySelector";
import { addCountryPaymentGateway, editCountryPaymentGateway, } from "../../redux/countryPaymentGatewaySlice";
import { fetchCountries } from "../../services/countryPaymentGatewayService";
import { toast } from "react-toastify";

const initialState = {
    countries: [],
    razorpay_enabled: false,
    stripe_enabled: false,
    paypal_enabled: false,
    is_active: true,
};

const CountryPaymentGatewayFormModal = ({
    open,
    onClose,
    data,
    reload,
    existingConfigurations = [],
}) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [countries, setCountries] = useState([]);

    const filteredCountries = countries.filter((country) => {
        const isAlreadyConfigured = existingConfigurations.some(
            (config) => config.country_code === country.code
        );
        // Keep current country while editing
        if (data?.country_code === country.code) {
            return true;
        }
        return !isAlreadyConfigured;
    });

    useEffect(() => {
        loadCountries();
    }, []);

    const loadCountries = async () => {
        try {
            const response = await fetchCountries();
            setCountries(response.data);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        if (open) {
            if (data) {
                setFormData({
                    ...initialState,
                    countries: [
                        {
                            country_name: data.country_name,
                            country_code: data.country_code,
                        },
                    ],
                    razorpay_enabled: data.razorpay_enabled,
                    stripe_enabled: data.stripe_enabled,
                    paypal_enabled: data.paypal_enabled,
                    is_active: data.is_active,
                });
            } else {
                setFormData(initialState);
            }

            setErrors({});
        }
    }, [open, data]);

    const toggleField = (field) => {
        setFormData((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const validate = () => {
        const validationErrors = {};

        if (formData.countries.length === 0) {
            validationErrors.countries = "Please select at least one country.";
        }

        formData.countries.forEach((country) => {
            if (!country.country_code) {
                validationErrors[country.country_name] =
                    `Country code is required for ${country.country_name}`;
            } else if (!/^[A-Za-z]{2}$/.test(country.country_code)) {
                validationErrors[country.country_name] =
                    `Country code for ${country.country_name} must be exactly 2 letters.`;
            }
        });

        setErrors(validationErrors);

        return Object.keys(validationErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);

        try {
            if (data) {
                const payload = {
                    razorpay_enabled: formData.razorpay_enabled,
                    stripe_enabled: formData.stripe_enabled,
                    paypal_enabled: formData.paypal_enabled,
                    is_active: formData.is_active,
                };
                await dispatch(
                    editCountryPaymentGateway({
                        id: data.id,
                        payload,
                    })
                ).unwrap();
                toast.success("Configuration updated successfully.");
            } else {
                const payload = {
                    countries: formData.countries,
                    razorpay_enabled: formData.razorpay_enabled,
                    stripe_enabled: formData.stripe_enabled,
                    paypal_enabled: formData.paypal_enabled,
                    is_active: formData.is_active,
                };
                await dispatch(
                    addCountryPaymentGateway(payload)
                ).unwrap();
                toast.success("Configuration created successfully.");
            }
            await reload();
            onClose();
        } catch (error) {
            toast.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <CountryPaymentGatewayModal
            isOpen={open}
            onClose={onClose}
            title={
                data
                    ? "Edit Country Payment Gateway"
                    : "Add Country Payment Gateway"
            }
            footer={
                <>
                    <Button
                        variant="secondary"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>

                    <Button
                        loading={loading}
                        onClick={handleSubmit}
                    >
                        {data ? "Update" : "Save"}
                    </Button>
                </>
            }
        >
            <div className="space-y-8">
                {data ? (
                    <div>
                        <label className="mb-2 block text-sm font-medium text-slate-700">
                            Country
                        </label>

                        <input
                            type="text"
                            readOnly
                            value={`${data.country_name} (${data.country_code})`}
                            className="w-full rounded-xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm text-slate-600 cursor-not-allowed"
                        />

                        <p className="mt-2 text-xs text-slate-500">
                            Country cannot be changed after creation.
                        </p>
                    </div>
                ) : (
                    <CountrySelector
                        countries={filteredCountries}
                        selectedCountries={formData.countries}
                        errors={errors}
                        onChange={(countries) => {
                            setFormData((prev) => ({
                                ...prev,
                                countries,
                            }));

                            setErrors({});
                        }}
                    />
                )}

                <div>
                    <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
                        Available Payment Gateways
                    </h3>

                    <div className="grid gap-3 sm:grid-cols-2">
                        <GatewayToggleCard
                            title="Razorpay"
                            description="Enable Razorpay payments"
                            checked={formData.razorpay_enabled}
                            onChange={() => toggleField("razorpay_enabled")}
                        />
                        <GatewayToggleCard
                            title="Stripe"
                            description="Enable Stripe payments"
                            checked={formData.stripe_enabled}
                            onChange={() => toggleField("stripe_enabled")}
                        />
                        <GatewayToggleCard
                            title="PayPal"
                            description="Enable PayPal payments"
                            checked={formData.paypal_enabled}
                            onChange={() => toggleField("paypal_enabled")}
                        />
                        <GatewayToggleCard
                            title="Active"
                            description="Configuration is active"
                            checked={formData.is_active}
                            onChange={() => toggleField("is_active")}
                        />
                    </div>
                </div>
            </div>
        </CountryPaymentGatewayModal>
    );
};

export default CountryPaymentGatewayFormModal;